import { z } from "zod";

export const PDF_TO_DOCX_MAX_FILE_SIZE = 25 * 1024 * 1024;
export const PDF_TO_DOCX_MAX_PAGE_COUNT = 200;
export const PDF_MEDIA_TYPE = "application/pdf";
export const DOCX_MEDIA_TYPE =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

export const capabilityIdSchema = z.enum([
  "pdf.to.docx",
  "pdf.merge",
  "pdf.split",
  "pdf.compress",
  "pdf.ocr",
  "docx.to.pdf",
  "images.to.pdf",
]);

export type CapabilityId = z.infer<typeof capabilityIdSchema>;

export const pdfToDocxOptionsSchema = z
  .object({
    ocr: z.enum(["auto", "disabled"]).default("auto"),
    language: z.enum(["auto", "zh-CN", "en-US"]).default("auto"),
    layoutMode: z
      .enum(["preserve-layout", "flowing-text"])
      .default("preserve-layout"),
  })
  .strict();

export type PdfToDocxOptions = z.infer<typeof pdfToDocxOptionsSchema>;

const noOptionsSchema = z.object({}).strict();

export interface CapabilityDefinition {
  readonly capabilityId: CapabilityId;
  readonly version: string;
  readonly acceptedInputFormats: readonly string[];
  readonly outputFormat: string;
  readonly maxInputs: number;
  readonly maxFileSize: number;
  readonly maxPageCount: number;
  readonly optionSchema: z.ZodType;
  readonly enabledEngines: readonly string[];
}

export const CAPABILITY_DEFINITIONS = {
  "pdf.to.docx": {
    capabilityId: "pdf.to.docx",
    version: "1.0.0",
    acceptedInputFormats: [PDF_MEDIA_TYPE],
    outputFormat: DOCX_MEDIA_TYPE,
    maxInputs: 1,
    maxFileSize: PDF_TO_DOCX_MAX_FILE_SIZE,
    maxPageCount: PDF_TO_DOCX_MAX_PAGE_COUNT,
    optionSchema: pdfToDocxOptionsSchema,
    enabledEngines: ["adobe-pdf-services", "mock-pdf-to-docx"],
  },
  "pdf.merge": {
    capabilityId: "pdf.merge",
    version: "1.0.0",
    acceptedInputFormats: [PDF_MEDIA_TYPE],
    outputFormat: PDF_MEDIA_TYPE,
    maxInputs: 20,
    maxFileSize: PDF_TO_DOCX_MAX_FILE_SIZE,
    maxPageCount: 1_000,
    optionSchema: noOptionsSchema,
    enabledEngines: [],
  },
  "pdf.split": {
    capabilityId: "pdf.split",
    version: "1.0.0",
    acceptedInputFormats: [PDF_MEDIA_TYPE],
    outputFormat: "application/zip",
    maxInputs: 1,
    maxFileSize: PDF_TO_DOCX_MAX_FILE_SIZE,
    maxPageCount: 1_000,
    optionSchema: noOptionsSchema,
    enabledEngines: [],
  },
  "pdf.compress": {
    capabilityId: "pdf.compress",
    version: "1.0.0",
    acceptedInputFormats: [PDF_MEDIA_TYPE],
    outputFormat: PDF_MEDIA_TYPE,
    maxInputs: 1,
    maxFileSize: PDF_TO_DOCX_MAX_FILE_SIZE,
    maxPageCount: 1_000,
    optionSchema: noOptionsSchema,
    enabledEngines: [],
  },
  "pdf.ocr": {
    capabilityId: "pdf.ocr",
    version: "1.0.0",
    acceptedInputFormats: [PDF_MEDIA_TYPE],
    outputFormat: PDF_MEDIA_TYPE,
    maxInputs: 1,
    maxFileSize: PDF_TO_DOCX_MAX_FILE_SIZE,
    maxPageCount: PDF_TO_DOCX_MAX_PAGE_COUNT,
    optionSchema: noOptionsSchema,
    enabledEngines: [],
  },
  "docx.to.pdf": {
    capabilityId: "docx.to.pdf",
    version: "1.0.0",
    acceptedInputFormats: [DOCX_MEDIA_TYPE],
    outputFormat: PDF_MEDIA_TYPE,
    maxInputs: 1,
    maxFileSize: PDF_TO_DOCX_MAX_FILE_SIZE,
    maxPageCount: PDF_TO_DOCX_MAX_PAGE_COUNT,
    optionSchema: noOptionsSchema,
    enabledEngines: [],
  },
  "images.to.pdf": {
    capabilityId: "images.to.pdf",
    version: "1.0.0",
    acceptedInputFormats: ["image/jpeg", "image/png", "image/webp"],
    outputFormat: PDF_MEDIA_TYPE,
    maxInputs: 50,
    maxFileSize: PDF_TO_DOCX_MAX_FILE_SIZE,
    maxPageCount: 50,
    optionSchema: noOptionsSchema,
    enabledEngines: [],
  },
} as const satisfies Record<CapabilityId, CapabilityDefinition>;

export const ENABLED_CAPABILITY_IDS = ["pdf.to.docx"] as const;

export function getCapabilityDefinition(
  capabilityId: CapabilityId,
): CapabilityDefinition {
  return CAPABILITY_DEFINITIONS[capabilityId];
}
